import i18n from "@/i18n";

/**
 * Erreur levée quand `invokeWithRetry` a rendu `authExpired` : la session est
 * expirée, la déconnexion et la redirection vers `/login` sont déjà lancées.
 *
 * Pourquoi une erreur plutôt qu'une valeur vide — plusieurs wrappers de lecture
 * traduisaient `authExpired` par `return []`. C'est un mensonge : « je n'ai pas
 * pu lire » se retrouvait encodé comme « il n'y a rien ». Sur le chemin du
 * quota de séances, ce mensonge devenait une affirmation fausse et bloquante :
 *
 *   getUserSubscriptions → []            (session expirée, pas « aucun abo »)
 *   getClientActiveSubscriptionByUserId → null
 *   WeeklySchedule : allSettled `fulfilled` → sessionsRemaining = 0
 *   ClassBookingDialog : « Vous avez atteint la limite de séances autorisées »
 *
 * Alors que l'adhérent avait peut-être 12 séances au compteur.
 *
 * Le `return []` avait une raison d'être : ne pas faire surgir un toast d'erreur
 * dans une vue qui est de toute façon en train de se démonter. On la préserve
 * sans le mensonge — l'erreur est levée, mais elle est reconnaissable, et les
 * `catch` d'interface consultent `isSessionExpiredError()` pour rester muets.
 */
export class SessionExpiredError extends Error {
  /**
   * Marqueur porté par l'instance plutôt que reposant sur `instanceof` :
   * l'héritage de `Error` est fragile après transpilation, et l'erreur peut
   * traverser des frontières (sérialisation d'un état, `Promise.allSettled`)
   * où la chaîne de prototypes n'est plus garantie.
   */
  readonly sessionExpired = true as const;

  constructor(functionName?: string) {
    super(
      functionName
        ? i18n.t("common:errors.sessionExpiredCallout", { functionName })
        : i18n.t("common:errors.sessionExpired")
    );
    this.name = 'SessionExpiredError';
    // Nécessaire pour que `instanceof` reste vrai quand la cible de compilation
    // est antérieure à ES2015 (Babel/TS cassent la chaîne sinon).
    Object.setPrototypeOf(this, SessionExpiredError.prototype);
  }
}

/**
 * À utiliser dans les `catch` d'interface : sur une session expirée, la
 * navigation vers `/login` est déjà en cours, il n'y a rien à annoncer à
 * l'utilisateur et un toast d'échec ne ferait que clignoter avant le démontage.
 */
export function isSessionExpiredError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { sessionExpired?: unknown }).sessionExpired === true
  );
}
